/**
 * MarketFit Solution Definition Page
 * Form tab + Agent tab for defining a product/solution (inputs and benefits).
 * Route: /dashboard/product/:productId/marketfit/solution/:solutionId
 * Note: solutionId is the product ID.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Tabs, Button, Input, Typography, Spin, message } from 'antd';
import {
  SolutionOutlined,
  RobotOutlined,
  FormOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import {
  getMarketFitProduct,
  updateMarketFitProduct,
  createMarketFitProduct,
} from '../services/localBridgeSessionApi';
import {
  parseProductInputsBenefits,
  parseProductSchemaV2,
  getMarketFitSchemaVersion,
  defaultProductSchemaV2,
  generateId,
  type ProductInputsBenefits,
  type ProductSchemaV2,
  type ProductInputItem,
  type ProductBenefitItem,
} from '../types/marketFitLocalBridge';
import { ProductDefinitionForm } from '../components/MarketFit/ProductDefinitionForm';
import { MarketFitAgentChat } from '../components/MarketFit/MarketFitAgentChat';
import { useUser } from '../contexts/UserContext';
import { getProductDomainSession } from '../services/productDomainSessionApi';

/** Merge agent-extracted v2 data into ProductSchemaV2 */
function mergeAgentDataIntoProductV2(
  current: ProductSchemaV2,
  agentData: Partial<ProductSchemaV2> & Record<string, unknown>,
  doMerge?: boolean
): ProductSchemaV2 {
  const result = { ...current };
  if (Array.isArray(agentData.functional_interventions) && agentData.functional_interventions.length > 0) {
    result.functional_interventions = doMerge
      ? [...current.functional_interventions, ...agentData.functional_interventions]
      : agentData.functional_interventions;
  }
  if (Array.isArray(agentData.asset_interactions) && agentData.asset_interactions.length > 0) {
    result.asset_interactions = doMerge
      ? [...current.asset_interactions, ...agentData.asset_interactions]
      : agentData.asset_interactions;
  }
  if (Array.isArray(agentData.emotional_interventions) && agentData.emotional_interventions.length > 0) {
    result.emotional_interventions = doMerge
      ? [...current.emotional_interventions, ...agentData.emotional_interventions]
      : agentData.emotional_interventions;
  }
  if (agentData.product_costs != null) {
    result.product_costs = agentData.product_costs as ProductSchemaV2['product_costs'];
  }
  return result;
}

const { Title, Text } = Typography;

/** Merge agent-extracted data into ProductInputsBenefits (legacy schema) */
function mergeAgentDataIntoProduct(
  current: ProductInputsBenefits,
  agentData: Record<string, unknown>
): ProductInputsBenefits {
  const toInput = (x: unknown): ProductInputItem => {
    const o = typeof x === 'object' && x !== null ? (x as Record<string, unknown>) : {};
    const cat = (['cost', 'skill', 'data', 'equipment', 'space', 'time'] as const).includes(
      o.category as 'cost'
    )
      ? (o.category as 'cost')
      : 'cost';
    return {
      id: typeof o.id === 'string' ? o.id : generateId(),
      name: typeof o.name === 'string' ? o.name : '',
      category: cat,
      isRequired: typeof o.isRequired === 'boolean' ? o.isRequired : true,
    };
  };

  const toBenefit = (x: unknown): ProductBenefitItem => {
    const o = typeof x === 'object' && x !== null ? (x as Record<string, unknown>) : {};
    const cat = (['cost', 'skill', 'data', 'equipment', 'space', 'time'] as const).includes(
      o.category as 'cost'
    )
      ? (o.category as 'cost')
      : 'cost';
    return {
      id: typeof o.id === 'string' ? o.id : generateId(),
      name: typeof o.name === 'string' ? o.name : '',
      category: cat,
    };
  };

  const inputs = Array.isArray(agentData.inputs)
    ? (agentData.inputs as unknown[]).map(toInput).filter((i) => i.name?.trim())
    : current.inputs;
  const benefits = Array.isArray(agentData.benefits)
    ? (agentData.benefits as unknown[]).map(toBenefit).filter((b) => b.name?.trim())
    : current.benefits;

  return {
    inputs: inputs.length > 0 ? inputs : current.inputs,
    benefits: benefits.length > 0 ? benefits : current.benefits,
  };
}

export const MarketFitSolutionPage: React.FC = () => {
  const { productId, solutionId } = useParams<{ productId: string; solutionId: string }>();
  const navigate = useNavigate();
  const { selectedCompany } = useUser();

  const [companyId, setCompanyId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [productName, setProductName] = useState('');
  const [data, setData] = useState<ProductInputsBenefits>({ inputs: [], benefits: [] });
  const [schemaMode, setSchemaMode] = useState<'legacy' | 'v2'>('legacy');
  const [dataV2, setDataV2] = useState<ProductSchemaV2>(defaultProductSchemaV2());
  const [activeTab, setActiveTab] = useState<string>('form');

  // Create flow state (when solutionId === 'new')
  const [newProductName, setNewProductName] = useState('');
  const [creating, setCreating] = useState(false);

  const isNew = solutionId === 'new';

  const resolveCompanyId = useCallback(async (): Promise<string | null> => {
    if (selectedCompany?.id) return selectedCompany.id;
    if (productId) {
      const session = await getProductDomainSession(productId);
      return session?.company_id ?? null;
    }
    return null;
  }, [selectedCompany?.id, productId]);

  const loadProduct = useCallback(async () => {
    if (!solutionId || isNew) return;
    setLoading(true);
    try {
      const cid = await resolveCompanyId();
      setCompanyId(cid);
      if (!cid) {
        setLoading(false);
        return;
      }
      const p = await getMarketFitProduct(solutionId);
      if (p) {
        setProductName(p.name);
        const json = p.inputs_benefits_json ?? '';
        setData(parseProductInputsBenefits(json));
        setDataV2(parseProductSchemaV2(json));
        setSchemaMode(getMarketFitSchemaVersion(json) === 2 ? 'v2' : 'legacy');
      } else {
        setProductName('');
        setData({ inputs: [], benefits: [] });
        setDataV2(defaultProductSchemaV2());
        setSchemaMode('legacy');
      }
    } finally {
      setLoading(false);
    }
  }, [solutionId, isNew, resolveCompanyId]);

  useEffect(() => {
    if (isNew) {
      resolveCompanyId().then(setCompanyId);
      setLoading(false);
    } else {
      loadProduct();
    }
  }, [isNew, loadProduct, resolveCompanyId]);

  const handleSave = async () => {
    if (!solutionId || isNew) return;
    const payload = {
      inputs_benefits_json:
        schemaMode === 'v2'
          ? JSON.stringify(dataV2)
          : JSON.stringify({
              inputs: data.inputs.filter((i) => i.name?.trim()),
              benefits: data.benefits.filter((b) => b.name?.trim()),
            }),
    };
    setSaving(true);
    try {
      const updated = await updateMarketFitProduct(solutionId, payload);
      if (updated) {
        message.success('Solution definition saved');
      } else {
        message.error('Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleApplyFromAgent = useCallback(
    (agentData: Record<string, unknown>, options?: { merge?: boolean }) => {
      const isV2 =
        (agentData as { schema_version?: number }).schema_version === 2 ||
        Array.isArray(agentData.functional_interventions) ||
        Array.isArray(agentData.asset_interactions) ||
        Array.isArray(agentData.emotional_interventions);
      if (schemaMode === 'v2' && isV2) {
        setDataV2((prev) => mergeAgentDataIntoProductV2(prev, agentData as Partial<ProductSchemaV2> & Record<string, unknown>, options?.merge));
      } else {
        setData((prev) => mergeAgentDataIntoProduct(prev, agentData));
      }
      setActiveTab('form');
      message.success('Agent suggestions applied to form');
    },
    [schemaMode]
  );

  const handleCreateProduct = async () => {
    const name = newProductName?.trim();
    if (!name || !companyId) {
      message.warning('Enter a product/solution name');
      return;
    }
    setCreating(true);
    try {
      const created = await createMarketFitProduct({ company_id: companyId, name });
      if (created && productId) {
        message.success('Solution created');
        navigate(`/dashboard/product/${productId}/marketfit/solution/${created.id}`, {
          replace: true,
        });
      } else {
        message.error('Failed to create solution');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleBack = () => {
    if (productId) {
      navigate(`/dashboard/product/${productId}/marketfit`);
    } else {
      navigate('/dashboard');
    }
  };

  if (!productId) {
    return (
      <Card>
        <Text type="secondary">No product selected.</Text>
        <Button type="link" onClick={() => navigate('/dashboard')}>
          Go to dashboard
        </Button>
      </Card>
    );
  }

  if (isNew) {
    return (
      <Card
        title={
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack}>
            Back
          </Button>
        }
      >
        <Title level={4}>Create solution</Title>
        <p>Enter a name for the new product/solution.</p>
        <div style={{ marginTop: 16, maxWidth: 400 }}>
          <Input
            placeholder="Solution name"
            value={newProductName}
            onChange={(e) => setNewProductName(e.target.value)}
            onPressEnter={handleCreateProduct}
          />
          <Button
            type="primary"
            loading={creating}
            onClick={handleCreateProduct}
            style={{ marginTop: 12 }}
          >
            Create
          </Button>
        </div>
        {!companyId && (
          <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
            Select a company in the dashboard to create a solution.
          </Text>
        )}
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <Spin spinning tip="Loading solution...">
          <div style={{ padding: 48, textAlign: 'center', minHeight: 100 }} />
        </Spin>
      </Card>
    );
  }

  if (!companyId) {
    return (
      <Card>
        <Text type="secondary">Company context required. Select a company in the dashboard.</Text>
        <Button type="link" onClick={handleBack}>
          Go back
        </Button>
      </Card>
    );
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack} />
          <SolutionOutlined />
          <span>Define solution: {productName || '…'}</span>
        </div>
      }
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        destroyOnHidden={false}
        items={[
          {
            key: 'form',
            label: (
              <span>
                <FormOutlined /> Form
              </span>
            ),
            children: (
              <ProductDefinitionForm
                data={data}
                dataV2={dataV2}
                schemaMode={schemaMode}
                onDataChange={setData}
                onDataV2Change={setDataV2}
                onSchemaModeChange={setSchemaMode}
                onSave={handleSave}
                loading={loading}
                saving={saving}
                productId={solutionId ?? null}
                showFooter={true}
              />
            ),
          },
          {
            key: 'agent',
            label: (
              <span>
                <RobotOutlined /> Agent
              </span>
            ),
            children: (
              <div style={{ marginTop: 16 }}>
                <MarketFitAgentChat
                  artifactType="product"
                  artifactId={solutionId ?? null}
                  companyId={companyId}
                  onApplyToForm={handleApplyFromAgent}
                  stateId="product_definition"
                  requiredFields={['inputs', 'benefits']}
                  userPrompt="Describe the product. What inputs does it require and what benefits does it provide?"
                  formData={
                    schemaMode === 'v2' ? dataV2 : { inputs: data.inputs, benefits: data.benefits }
                  }
                />
              </div>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default MarketFitSolutionPage;
